import Bugsnag from '@bugsnag/js'
import axios from 'axios'
import 'core-js/stable/promise'
import cookie from 'react-cookies'

import { getAPIUrl } from './api'

declare var dataLayer: any
declare const BRAND: string
declare const WP_NONCE: string

export interface IOrderInfo {
  productId: number | string
  pricingQuoteId: string
  materialId: string
  quantity: number
  widthInUnit: number
  heightInUnit: number
  widthMM: number
  heightMM: number
  units: 'cm' | 'in'
  currency: string
  instructions: string
}

export interface Currency {
  code: string
}

export interface Option {
  name: string
  nameId: number
  value: string
  valueId?: number
}

export interface PhysicalItem {
  id: string
  parent_id?: any
  variant_id: number
  product_id: number
  sku: string
  name: string
  url: string
  quantity: number
  taxable: boolean
  image_url: string
  discounts: any[]
  coupons: any[]
  discount_amount: number
  coupon_amount: number
  list_price: number
  sale_price: number
  extended_list_price: number
  extended_sale_price: number
  is_require_shipping: boolean
  is_mutable: boolean
  options: Option[]
}

export interface LineItems {
  physical_items: PhysicalItem[]
  digital_items: any[]
  gift_certificates: any[]
  custom_items: any[]
}

export interface CartResponsePayload {
  id: string
  customer_id: number
  channel_id: number
  email: string
  currency: Currency
  tax_included: boolean
  base_amount: number
  discount_amount: number
  cart_amount: number
  coupons: any[]
  line_items: LineItems
  created_time: Date
  updated_time: Date
}

export type IPricingInfo = Record<string, any>

interface IAddToCartResponse {
  cartId: string
  cartAnalyticsToken?: string
}

const setLocalVars = (data: IAddToCartResponse) => {
  if (data.cartId === 'undefined') {
    return data
  }

  cookie.save('wp-bigcommerce_cart_id', data.cartId, {
    expires: new Date(new Date().getTime() + 86400 * 7 * 1000),
    secure: true,
    httpOnly: true,
    path: '/',
  })

  localStorage.setItem('si_cartId', data.cartId)

  if (data.cartAnalyticsToken) {
    localStorage.setItem('analyticsToken', data.cartAnalyticsToken)
  }

  const itemCount = parseInt(
    cookie.load('wp-bigcommerce_cart_item_count') || '0',
    10
  )

  cookie.save('wp-bigcommerce_cart_item_count', itemCount + 1, {
    expires: new Date(new Date().getTime() + 86400 * 7 * 1000),
    secure: true,
    httpOnly: true,
    path: '/',
  })

  return data
}

interface CartInfoPayload {
  cartId: string | null
}

export default () => {
  const resetId = () => {
    localStorage.removeItem('si_cartId')
    cookie.save('wp-bigcommerce_cart_id', 'x', {
      expires: new Date(new Date().getTime() - 1),
      secure: true,
      httpOnly: true,
      path: '/',
    })
    cookie.save('wp-bigcommerce_cart_id', 'x', {
      expires: new Date(new Date().getTime() - 1),
      secure: true,
      httpOnly: false,
      path: '/',
    })
  }

  const getId = (ignoreLocal = false) => {
    if (!ignoreLocal) {
      const fromLocal = localStorage.getItem('si_cartId')

      if (fromLocal && fromLocal !== null) {
        return Promise.resolve(fromLocal)
      }
    }

    return axios
      .get<CartInfoPayload>(`/wp-json/${BRAND}/v1/cart/info`)
      .then((resp) => {
        if (resp.data.cartId && resp.data.cartId !== null) {
          localStorage.setItem('si_cartId', resp.data.cartId)
        }

        return resp.data.cartId
      })
  }

  const get = (id: string) => {
    return getAPIUrl('/cart').then((cartUrl) => {
      return axios.get<CartResponsePayload>(
        `${cartUrl}/${id}?${new Date().getTime()}`,
        {
          method: 'GET',
        }
      )
    })
  }

  const getWPCartUrl = () => {
    return `/wp-json/bigcommerce/v1/cart`
  }

  const addToCart = (
    orderInfo: IOrderInfo,
    pricingInfo: IPricingInfo,
    fileIds: string[],
    cartId: string,
    channelId: number,
    name: string
  ): Promise<void> => {
    return getAPIUrl('/add-to-cart').then((addToCartUrl) => {
      return (
        axios
          .post<IAddToCartResponse>(
            addToCartUrl,
            {
              productId: orderInfo.productId,
              cartId,
              channelId,

              orderInfo,
              pricingInfo,
              fileIds,
            },
            {
              timeout: 10000,
              headers: {
                'X-WP-Nonce': WP_NONCE,
              },
            }
          )
          // .then((resp) => {
          //   return syncCartId(resp.data.cartId).then(() => {
          //     return resp.data
          //   })
          // })
          .then((resp) => setLocalVars(resp.data))
          .then((_data) => {
            return new Promise<void>((resolve) => {
              dataLayer.push({
                event: 'addToCart',
                ecommerce: {
                  currencyCode: orderInfo.currency,
                  add: {
                    products: [
                      {
                        id: pricingInfo.sku,
                        name,
                        brand: 'Sticker It',
                        variant: orderInfo.materialId, // this is the material
                        price:
                          pricingInfo.price.priceIncVAT / orderInfo.quantity,
                        quantity: orderInfo.quantity,
                      },
                    ],
                  },
                },
                eventCallback: () => {
                  return resolve()
                },
                eventTimeout: 1500,
              })
            })
          })
          .catch((err) => {
            Bugsnag.notify(err, (e) => {
              e.addMetadata('orderInfo', orderInfo)
              e.addMetadata('pricingInfo', pricingInfo)
              e.addMetadata('fileIds', fileIds)
              e.addMetadata('name', { name })
            })

            throw err
          })
      )
    })
  }

  const remove = (cartId: string, itemId: string) => {
    return getAPIUrl('/cart').then((cartUrl) => {
      return axios.delete(`${cartUrl}/${cartId}/items/${itemId}`)
    })
  }

  const reorder = (
    cartId: string,
    channelId: number,
    orderId: number | string
  ): Promise<void> => {
    return getAPIUrl('/reorder').then((reorderUrl) => {
      return axios
        .post<IAddToCartResponse>(reorderUrl, {
          cartId,
          channelId,
          orderId,
        })
        .then((resp) => setLocalVars(resp.data))
        .then(() => {})
    })
  }

  /*
          Sample

  {
  "cart_id": "baee1820-4e87-4bf7-83c8-3334ad1d859c",
  "base_amount": { "raw": 34.99, "formatted": "\u00a334.99" },
  "discount_amount": { "raw": 0, "formatted": "Free" },
  "cart_amount": { "raw": 41.98, "formatted": "\u00a341.98" },
  "tax_included": true,
  "items": {
  "d4b4bfe1-80cb-47a6-b4a6-0c5676bdd512": {
  "id": "d4b4bfe1-80cb-47a6-b4a6-0c5676bdd512",
  "variant_id": 77,
  "product_id": 112,
  "name": "Die cut stickers",
  "quantity": 1,
  "list_price": { "raw": 34.17, "formatted": "\u00a334.17" },
  "sale_price": { "raw": 41, "formatted": "\u00a341.00" },
  "total_list_price": { "raw": 34.17, "formatted": "\u00a334.17" },
  "total_sale_price": { "raw": 41, "formatted": "\u00a341.00" },
  "post_id": 1609,
  "thumbnail_id": 1611,
  "is_featured": false,
  "on_sale": false,
  "show_condition": false,
  "sku": {
  "product": "STICKER-CUSTOM-DIE",
  "variant": "STICKER-CUSTOM-DIE"
  },
  "options": [],
  "minimum_quantity": 0,
  "maximum_quantity": 0,
  "inventory_level": -1,
  "bigcommerce_availability": [
  { "id": 13, "label": "available", "slug": "available" }
  ],
  "bigcommerce_condition": [{ "id": 14, "label": "New", "slug": "new" }],
  "bigcommerce_product_type": [
  { "id": 15, "label": "physical", "slug": "physical" }
  ],
  "bigcommerce_brand": [],
  "bigcommerce_category": [
  { "id": 7, "label": "Most popular", "slug": "popular-stickers" },
  {
  "id": 9,
  "label": "Packaging stickers",
  "slug": "packaging-stickers"
  },
  { "id": 25, "label": "Shape", "slug": "sticker-shapes" },
  { "id": 4, "label": "Shop all", "slug": "all-stickers" }
  ]
  },
  "0f450aa0-49cb-4408-9270-f26a41e4520f": {
  "id": "0f450aa0-49cb-4408-9270-f26a41e4520f",
  "variant_id": 152,
  "product_id": 187,
  "name": "Sticker sample pack",
  "quantity": 1,
  "list_price": { "raw": 0.82, "formatted": "\u00a30.82" },
  "sale_price": { "raw": 0.98, "formatted": "\u00a30.98" },
  "total_list_price": { "raw": 0.82, "formatted": "\u00a30.82" },
  "total_sale_price": { "raw": 0.98, "formatted": "\u00a30.98" },
  "post_id": 2564,
  "thumbnail_id": 2566,
  "is_featured": false,
  "on_sale": false,
  "show_condition": false,
  "sku": {
  "product": "SAMPLE-PACK-STICKER-STD",
  "variant": "SAMPLE-PACK-STICKER-STD"
  },
  "options": [],
  "minimum_quantity": 0,
  "maximum_quantity": 0,
  "inventory_level": -1,
  "bigcommerce_availability": [
  { "id": 13, "label": "available", "slug": "available" }
  ],
  "bigcommerce_condition": [{ "id": 14, "label": "New", "slug": "new" }],
  "bigcommerce_product_type": [
  { "id": 15, "label": "physical", "slug": "physical" }
  ],
  "bigcommerce_brand": [
  { "id": 27, "label": "Sticker it", "slug": "sticker-it" }
  ],
  "bigcommerce_category": [
  { "id": 26, "label": "Samples", "slug": "samples" }
  ]
  }
  },
  "tax_amount": { "raw": 6.989999999999995, "formatted": "\u00a36.99" },
  "subtotal": { "raw": 41.98, "formatted": "\u00a341.98" }
  }


          */
  const addSimpleProduct = (
    cartId: string,
    productId: number,
    quantity: number,
    sku: string,
    name: string,
    price: number
  ): Promise<any> => {
    const CART_API_BASE = '/wp-json/bigcommerce/v1/cart'
    const url = cartId ? `${CART_API_BASE}/${cartId}` : CART_API_BASE

    const cartItem = {
      product_id: productId,
      variant: '',
      options: {},
      quantity,
    }

    const qs = new URLSearchParams()
    Object.entries(cartItem).forEach(([key, value]) => {
      if (!value || (typeof value === 'string' && value.length === 0)) {
        return
      }

      if (key === 'options') {
        Object.entries(value).forEach(([objectKey, objectValue], index) => {
          Object.entries(objectValue).forEach(([objKey, objValue]) => {
            qs.append(objKey, objValue as string)
          })
        })

        return
      }

      qs.set(key, value.toString())
    })

    const fullUrl = url + '?' + qs.toString()

    return axios
      .post<IAddToCartResponse>(fullUrl, null, {
        headers: {
          'X-WP-Nonce': WP_NONCE,
        },
      })
      .then(() =>
        setLocalVars({
          cartId,
        })
      )
      .then<void>((_data) => {
        return new Promise((resolve) => {
          dataLayer.push({
            event: 'addToCart',
            ecommerce: {
              currencyCode: 'GBP',
              add: {
                products: [
                  {
                    id: sku,
                    name,
                    brand: 'Sticker It',
                    variant: '',
                    price: price / quantity,
                    quantity: quantity,
                  },
                ],
              },
            },
            eventCallback: () => {
              resolve()
            },
            eventTimeout: 1500,
          })
        })
      })
      .catch((err) => {
        console.error(err)
        Bugsnag.notify(err)
        throw err
      })
  }

  const setSimpleQuantity = (
    cartId: string,
    itemId: string,
    productId: number,
    newQuantity: number
  ): Promise<void> => {
    const url = getWPCartUrl()
    const qs = new URLSearchParams()
    qs.set('cart_id', cartId)
    // qs.set('item_id', itemId)
    qs.set('product_id', productId.toString())
    qs.set('quantity', newQuantity.toString())

    const fullUrl = `${url}/${cartId}/items/${itemId}?${qs.toString()}`

    return axios.put(fullUrl)
  }

  const syncCartId = (cartId: string) => {
    const fullUrl = '/wp-json/stickerit/v1/cart'

    return axios.post(
      fullUrl,
      { cartId },
      {
        headers: {
          'X-WP-Nonce': WP_NONCE,
        },
      }
    )
  }

  return {
    resetId,
    getId,
    get,
    addToCart,
    addSimpleProduct,
    remove,
    reorder,
    setSimpleQuantity,
    syncCartId,
  }
}
